import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Dropdown, InlineNotification } from '@carbon/react';
import { ShoppingCart, View, Add, Checkmark } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useProducts } from '../hooks/useProducts';
import { toWebpImageUrl } from '../utils/imageUrl';
import { buildCartItem, isProductInStock } from '../utils/cartItem';

const ShopPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { addToCart, cartCount } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [addedProduct, setAddedProduct] = useState(null);

  // Map category display names to database values
  const categoryMap = {
    'all': 'all',
    'Sensors': 'sensor',
    'Gateways': 'gateway',
    'Bundles': 'bundle',
    'Probes': 'probes',
  };

  // Fetch products from Supabase - match React Native implementation
  const categoryFilter = categoryMap[selectedCategory] || selectedCategory.toLowerCase();
  const { products, isLoading } = useProducts(categoryFilter);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Categories matching React Native implementation
  const categories = ['all', 'Sensors', 'Gateways', 'Bundles', 'Probes'];

  // Filter products by search term
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Format category name for display
  const formatCategoryName = (category) => {
    if (category === 'all') return 'All';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const handleAddToCart = (product) => {
    if (!isProductInStock(product)) return;
    const cartProduct = buildCartItem(product);
    if (!cartProduct) return;
    addToCart(cartProduct);
    setAddedProduct(product.id);
    setTimeout(() => setAddedProduct(null), 2000);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="shop-page">
      <style>{`
        /* Mobile responsive styles for Shop page */
        @media (max-width: 671px) {
          /* Center all content on mobile */
          [data-testid="shop-page"] .cds--grid {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          /* Center header text */
          .shop-header-section .cds--grid {
            max-width: 100% !important;
          }

          .shop-header-section h1 {
            font-size: 2rem !important;
          }

          .shop-header-section p {
            font-size: 1rem !important;
          }

          /* Center filters section */
          .shop-filters-section .cds--grid {
            max-width: 100% !important;
          }

          .shop-filters-section > div > div > div {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .shop-filters-section > div > div > div > div {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
          }

          /* Center product cards */
          .shop-products-section .cds--grid {
            max-width: 100% !important;
            display: flex !important;
            justify-content: center !important;
          }

          .shop-products-section .cds--row {
            width: 100% !important;
            max-width: 100% !important;
            justify-content: center !important;
            margin: 0 !important;
          }

          .shop-products-section .cds--col {
            max-width: 100% !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }

          /* Product cards mobile styling */
          [data-testid^="product-"] {
            margin: 0 auto 24px !important;
            max-width: 400px !important;
          }

          [data-testid^="product-"] > div {
            padding: 20px !important;
          }

          [data-testid^="product-"] h3 {
            font-size: 0.875rem !important;
            min-height: auto !important;
          }

          [data-testid^="product-"] img {
            height: 100px !important;
          }

          /* Notification positioning on mobile */
          [data-testid="shop-page"] > div[style*="position: fixed"] {
            right: 16px !important;
            left: 16px !important;
            max-width: calc(100% - 32px) !important;
          }
        }

        @media (max-width: 480px) {
          /* Extra small screens */
          .shop-header-section {
            padding: 40px 0 !important;
          }

          .shop-header-section h1 {
            font-size: 1.75rem !important;
          }

          .shop-filters-section {
            padding: 16px 0 !important;
          }

          [data-testid^="product-"] {
            max-width: 100% !important;
          }

          [data-testid^="product-"] > div {
            padding: 16px !important;
          }

          [data-testid^="product-"] img {
            height: 80px !important;
          }
        }
      `}</style>
      {/* Header */}
      <section style={{ backgroundColor: '#0f62fe', padding: '60px 0' }} className="shop-header-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', color: '#ffffff' }}>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '16px' }}>Shop</h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>Quality sensors and IoT devices for your monitoring needs</p>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Filters */}
      <section style={{ backgroundColor: bgSecondary, padding: '24px 0', borderBottom: `1px solid ${borderColor}` }} className="shop-filters-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <Search
                  labelText="Search products"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="shop-search"
                />
              </div>
              <div style={{ minWidth: '180px' }}>
                <Dropdown
                  id="category-dropdown"
                  titleText="Category"
                  label="Select category"
                  items={categories.map(cat => formatCategoryName(cat))}
                  selectedItem={formatCategoryName(selectedCategory)}
                  onChange={({ selectedItem }) => {
                    // Find the key from the display name
                    const categoryKey = Object.keys(categoryMap).find(
                      key => formatCategoryName(key) === selectedItem
                    ) || selectedItem.toLowerCase();
                    setSelectedCategory(categoryKey);
                  }}
                  data-testid="category-filter"
                />
              </div>
              <div>
                <Button
                  kind="primary"
                  renderIcon={ShoppingCart}
                  onClick={() => navigate('/cart')}
                  data-testid="view-cart-button"
                >
                  View Cart ({cartCount})
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Added to Cart Notification */}
      {addedProduct && (
        <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999 }}>
          <InlineNotification
            kind="success"
            title="Added to cart!"
            subtitle="Product has been added to your cart"
            hideCloseButton
            style={{ maxWidth: '300px' }}
          />
        </div>
      )}

      {/* Products Grid */}
      <section style={{ padding: '48px 0' }} className="shop-products-section">
        {isLoading ? (
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <p style={{ color: textSecondary }}>Loading products...</p>
              </div>
            </Column>
          </Grid>
        ) : filteredProducts.length === 0 ? (
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <p style={{ color: textSecondary, marginBottom: '16px' }}>No products found</p>
                <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                  Try selecting a different category or check back later.
                </p>
              </div>
            </Column>
          </Grid>
        ) : (
          <Grid narrow>
            {filteredProducts.map((product) => (
            <Column lg={4} md={4} sm={4} key={product.id} style={{ marginBottom: '32px' }}>
              <div
                className="feature-card"
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  padding: '24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-${product.id}`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = isDark ? '0 8px 24px rgba(255,255,255,0.1)' : '0 8px 24px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Badges - fix alignment */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {product.featured && (
                    <Tag type="red">Featured</Tag>
                  )}
                  <Tag type="blue">{formatCategoryName(product.category)}</Tag>
                  <Tag type={isProductInStock(product) ? 'green' : 'red'}>
                    {isProductInStock(product) ? 'In Stock' : 'Out of Stock'}
                  </Tag>
                </div>
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 0 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '180px'
                }}>
                  {product.image_url || product.image ? (
                    <img
                      src={toWebpImageUrl(product.image_url || product.image)}
                      alt={product.name}
                      style={{ 
                        height: '120px', 
                        width: 'auto',
                        maxWidth: '100%',
                        objectFit: product.name.toLowerCase().includes('premium bundle') || 
                                  product.name.toLowerCase().includes('blogm') ? 'contain' : 'contain',
                        objectPosition: 'center',
                        margin: '0 auto'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{ 
                      height: '120px', 
                      width: '100%',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      backgroundColor: bgSecondary 
                    }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem' }}>No image</p>
                    </div>
                  )}
                </div>
                <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '8px', minHeight: '48px' }}>
                  {product.name}
                </h3>
                {product.description && (
                  <p 
                    style={{ 
                      color: textSecondary, 
                      fontSize: '0.875rem', 
                      marginBottom: '16px', 
                      flex: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.4'
                    }}
                    title={product.description}
                  >
                    {product.description}
                  </p>
                )}
                {product.features && product.features.length > 0 && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
                    {product.features.slice(0, 2).map((feature, idx) => (
                      <li key={idx} style={{ color: textSecondary, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <Checkmark size={12} style={{ color: '#24a148' }} /> {typeof feature === 'string' ? feature : JSON.stringify(feature)}
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                  <span style={{ color: '#0f62fe', fontSize: '1.5rem', fontWeight: 600 }}>{formatPrice(product.price)}</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={View}
                      iconDescription="View details"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/product/${product.id}`);
                      }}
                      data-testid={`view-product-${product.id}`}
                    />
                    <Button
                      kind={addedProduct === product.id ? 'secondary' : 'primary'}
                      size="sm"
                      renderIcon={addedProduct === product.id ? Checkmark : Add}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                      disabled={!isProductInStock(product)}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {addedProduct === product.id ? 'Added!' : 'Add to Cart'}
                    </Button>
                  </div>
                </div>
              </div>
            </Column>
          ))}
          </Grid>
        )}
      </section>
    </div>
  );
};

export default ShopPage;
